(() => {
  'use strict';
  if (window.BluvvyBundleOptions) return;
  const cache = new Map();
  const requests = new Map();
  const patched = new WeakSet();
  const modalLoads = new WeakMap();
  const modalSelector = '.gbbProductVariantModalContainer[data-product-id]';
  const cleanId = value => String(value || '').split('/').pop();
  const truthy = value => value === true || value === 'true';

  function readJSON(source, start) {
    while (/\s/.test(source[start] || '') && start < source.length) start++;
    let depth = 0, quoted = false, escaped = false;
    if (source[start] !== '{' && source[start] !== '[') throw new Error('Invalid Aris option data.');
    for (let i = start; i < source.length; i++) {
      const c = source[i];
      if (quoted) {
        if (escaped) escaped = false;
        else if (c === '\\') escaped = true;
        else if (c === '"') quoted = false;
      } else if (c === '"') quoted = true;
      else if (c === '{' || c === '[') depth++;
      else if ((c === '}' || c === ']') && --depth === 0) return JSON.parse(source.slice(start, i + 1));
    }
    throw new Error('Incomplete Aris option data.');
  }

  function readSets(html) {
    const sets = [];
    const pattern = /(?:window\.)?ap_front_settings\.config\[['"]optionset['"]\]\.push\(\s*/g;
    let match;
    while ((match = pattern.exec(html))) sets.push(readJSON(html, pattern.lastIndex));
    if (!html.includes('ap_front_settings')) throw new Error('Aris data could not be read.');
    return sets;
  }

  function convert(sets, product) {
    const fields = [];
    const keys = new Set();
    for (const set of sets) {
      if (set.type === 'manual' && set.products?.length && !set.products.some(id => cleanId(id) === cleanId(product.id || product.productId))) continue;
      for (const option of set.options || []) {
        if (truthy(option.is_hidden)) continue;
        if (truthy(option.is_inventory) || option.conditional_logic?.condition_items?.length || option.conditional_logic?.conditions?.length) {
          throw new Error('Conditional or inventory-linked options require an additional adapter.');
        }
        const values = option.option_values || [];
        if (values.some(value => Number(value.price || 0) !== 0 || ['createcharge', 'useexistingvariant', 'chargeorbundle', 'percentagecharge'].includes(value.type))) {
          throw new Error('Priced options require an additional adapter.');
        }
        const key = String(option.label_cart || option.label_product || '').trim();
        if (!key || keys.has(key)) throw new Error('Duplicate or missing customization property name.');
        keys.add(key);
        const field = {
          key,
          label: String(option.label_product || key).replace(/lenght|leight/ig, 'length'),
          isEnabled: true,
          isMandatory: truthy(option.is_required) || truthy(option.required),
          errorMessage: 'Please complete this option.',
          config: {},
          _bluvvyAris: true,
          _bluvvyDefault: ''
        };
        if (['text', 'textarea', 'text_area'].includes(option.type)) {
          field.type = 'TEXT';
          const config = option.text_config || option.config || option;
          field.config.placeholder = config.placeholder || option.placeholder || '';
          const max = config.max_characters ?? config.max_length ?? config.maxChars;
          const min = config.min_characters ?? config.min_length ?? config.minChars;
          if (max != null && Number.isFinite(Number(max))) field.config.maxChars = Number(max);
          if (min != null && Number.isFinite(Number(min))) field.config.minChars = Number(min);
          field._bluvvyMode = option.type === 'text' ? 'text' : 'textarea';
        } else if (['select', 'radio', 'swatch', 'swatch_select_image', 'swatch_image', 'swatch_color'].includes(option.type)) {
          field.type = 'RADIO';
          field.config.options = values.filter(value => value.value != null).map(value => ({
            value: String(value.value),
            label: String(value.value).replace(/\s*inches?\b|["″]/gi, "''").trim(),
            imageUrl: value.swatch?.file_image_url || '',
            color: value.swatch?.color || '',
            disabled: truthy(value.disabled) || value.available === false
          }));
          field._bluvvyMode = /birthstone|\bstone\b/i.test(field.label) || field.config.options.some(value => value.imageUrl) ? 'stone' : 'chips';
          const selected = values.find(value => truthy(value.is_selected) || truthy(value.selected) || truthy(value.is_default));
          field._bluvvyDefault = selected ? String(selected.value) : field.config.options.find(value => !value.disabled)?.value || '';
        } else if (['file', 'file_upload', 'upload'].includes(option.type)) {
          field.type = 'FILE_UPLOAD';
          field.config.acceptedTypes = option.accepted_types || option.file_types || [];
          if (!Array.isArray(field.config.acceptedTypes)) field.config.acceptedTypes = String(field.config.acceptedTypes).split(',').map(value => value.trim()).filter(Boolean);
          field.config.maxSizeMb = Number(option.max_file_size || option.max_size_mb || 20);
        } else {
          throw new Error('Unsupported Aris option type: ' + option.type);
        }
        fields.push(field);
      }
    }
    const rank = field => /length|size/i.test(field.label) ? 0 : /stone/i.test(field.label) ? 1 : 2;
    return fields.sort((a, b) => rank(a) - rank(b));
  }

  async function loadProduct(product) {
    const id = cleanId(product.productId || product.id);
    if (cache.has(id)) return cache.get(id);
    if (requests.has(id)) return requests.get(id);
    const request = (async () => {
      if (!product.handle) throw new Error('Product handle is missing.');
      const localeRoot = window.Shopify?.routes?.root || '/';
      const url = new URL(localeRoot + 'products/' + encodeURIComponent(product.handle), location.origin);
      const response = await fetch(url.href, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Product options could not load.');
      const html = await response.text();
      const fields = convert(readSets(html), product);
      cache.set(id, fields);
      return fields;
    })().finally(() => requests.delete(id));
    requests.set(id, request);
    return request;
  }

  function make(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text != null) element.textContent = text;
    return element;
  }

  function renderControl(app, field) {
    const component = app.components.customFields;
    const value = component.f.getValue(field.key) || '';
    if (field.type === 'TEXT') {
      const wrapper = make('div', 'bluvvy-bundle-text-wrap');
      const input = make(field._bluvvyMode === 'textarea' ? 'textarea' : 'input', 'bluvvy-bundle-input');
      if (input.tagName === 'INPUT') input.type = 'text';
      input.value = value;
      input.placeholder = field.config.placeholder || '';
      input.setAttribute('aria-label', field.label);
      input.required = field.isMandatory;
      if (field.config.maxChars != null) input.maxLength = field.config.maxChars;
      if (field.config.minChars != null) input.minLength = field.config.minChars;
      input.addEventListener('input', () => component.f.setValue(field.key, input.value));
      wrapper.append(input);
      return wrapper;
    }
    const choices = field.config.options;
    const isStone = field._bluvvyMode === 'stone';
    const group = make(isStone ? 'details' : 'div', isStone ? 'bluvvy-bundle-stone-dropdown' : 'bluvvy-bundle-chips');
    const menu = isStone ? make('div', 'bluvvy-bundle-stone-menu') : group;
    menu.setAttribute('role', 'group');
    menu.setAttribute('aria-label', field.label);
    const summary = isStone ? make('summary', 'bluvvy-bundle-stone-trigger') : null;
    const copy = (choice, target) => {
      target.replaceChildren();
      if (choice?.imageUrl) {
        const image = make('img', 'bluvvy-bundle-stone-image');
        image.src = choice.imageUrl;
        image.alt = '';
        target.append(image);
      }
      target.append(make('span', '', choice?.label || 'Choose'));
    };
    const sync = () => {
      const selected = component.f.getValue(field.key);
      menu.querySelectorAll('button').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.value === selected)));
      if (summary) copy(choices.find(choice => choice.value === selected), summary);
    };
    for (const choice of choices) {
      const button = make('button', isStone ? 'bluvvy-bundle-stone-choice' : '');
      button.type = 'button';
      button.dataset.value = choice.value;
      button.disabled = choice.disabled;
      copy(choice, button);
      button.addEventListener('click', () => {
        component.f.setValue(field.key, choice.value);
        sync();
        if (summary) { group.open = false; summary.focus(); }
      });
      menu.append(button);
    }
    if (summary) {
      group.append(summary, menu);
      group.addEventListener('keydown', event => {
        if (event.key === 'Escape') { group.open = false; summary.focus(); }
      });
    }
    sync();
    return group;
  }

  function patch(app) {
    const sdk = app.sdk;
    if (patched.has(sdk)) return;
    sdk.customFields.state.bundleHasCustomFields = true;
    const cf = sdk.customFields.f;
    const oldFields = cf.getProductFields;
    cf.getProductFields = function (context = {}) {
      const native = oldFields.call(this, context) || [];
      const additional = cache.get(cleanId(context.productId)) || [];
      const keys = new Set(native.map(field => field.key));
      return native.concat(additional.filter(field => !keys.has(field.key)));
    };
    const oldHasFields = cf.productHasFields;
    cf.productHasFields = function (context = {}) {
      const id = cleanId(context.productId);
      return !cache.has(id) || !!cache.get(id)?.length || oldHasFields.call(this, context);
    };
    const component = app.components.customFields;
    const oldControl = component.f.renderControl;
    component.f.renderControl = function (field) {
      return field._bluvvyAris && field.type !== 'FILE_UPLOAD' ? renderControl(app, field) : oldControl.call(this, field);
    };
    const oldUI = component.f.buildCustomFieldsUI;
    component.f.buildCustomFieldsUI = function (context = {}) {
      const values = { ...(context.fieldValues || {}) };
      for (const field of cf.getProductFields.call(sdk, context)) {
        if (field._bluvvyAris && !(field.key in values)) values[field.key] = field._bluvvyDefault;
      }
      const ui = oldUI.call(this, { ...context, fieldValues: values });
      if (ui && cf.getProductFields.call(sdk, context).some(field => field._bluvvyAris)) ui.classList.add('bluvvy-bundle-options');
      return ui;
    };
    const oldValidation = cf.validateFields;
    cf.validateFields = function (context = {}) {
      const result = oldValidation.call(this, context);
      for (const field of context.fields || cf.getProductFields.call(sdk, context)) {
        if (!field._bluvvyAris || field.type !== 'RADIO') continue;
        const value = context.fieldValues?.[field.key];
        if (value && !field.config.options.some(choice => choice.value === value && !choice.disabled)) {
          result.ok = false;
          result.errors ||= {};
          result.errors[field.key] = 'Please select an available option.';
        }
      }
      return result;
    };
    patched.add(sdk);
  }

  function status(modal, text) {
    let node = modal.querySelector('.bluvvy-bundle-options-status');
    if (!node) {
      node = make('div', 'bluvvy-bundle-options-status');
      node.setAttribute('role', 'status');
      modal.querySelector('.gbbVariantModalActionContainer')?.before(node);
    }
    node.textContent = text;
    return node;
  }

  async function prepare(modal, app) {
    const state = app.components.variantModal.state;
    const product = state.product;
    if (!product || cleanId(product.productId || product.id) !== modal.dataset.productId) return;
    const id = modal.dataset.productId;
    const record = { id, state: 'loading' };
    modalLoads.set(modal, record);
    modal.dataset.bluvvyOptionsState = 'loading';
    status(modal, 'Loading options…');
    try {
      await loadProduct(product);
      if (!modal.isConnected || modalLoads.get(modal) !== record || modal.dataset.productId !== id) return;
      const current = app.components.variantModal.state;
      if (cleanId(current.product?.productId || current.product?.id) !== id) return;
      app.components.variantModal.f.refreshCustomFields();
      app.components.variantModal.f.syncInstanceId();
      app.components.variantModal.f.refreshChargePrice();
      app.components.variantModal.f.refreshButtonStates();
      modal.querySelector('.bluvvy-bundle-options-status')?.remove();
      modal.dataset.bluvvyOptionsState = 'ready';
      record.state = 'ready';
    } catch (error) {
      record.state = 'error';
      modal.dataset.bluvvyOptionsState = 'error';
      const node = status(modal, 'Options could not load. ');
      const retry = make('button', 'bluvvy-bundle-retry', 'Try again');
      retry.type = 'button';
      retry.addEventListener('click', () => prepare(modal, app));
      node.append(retry);
      console.error('[Bluvvy bundle options]', error);
    }
  }

  function scan() {
    const app = window.gbb;
    if (!app?.sdk?.customFields?.f || !app.components?.variantModal?.state) return;
    patch(app);
    document.querySelectorAll(modalSelector).forEach(modal => {
      if (modalLoads.get(modal)?.id === modal.dataset.productId) return;
      prepare(modal, app);
    });
  }

  document.addEventListener('click', event => {
    const action = event.target.closest?.('.gbbProductVariantAddButton');
    if (!action) return;
    const modal = action.closest(modalSelector);
    if (modal && modal.dataset.bluvvyOptionsState !== 'ready') {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (!modal.querySelector('.bluvvy-bundle-options-status')) status(modal, 'Loading options…');
      scan();
    }
  }, true);
  let queued = false;
  const observer = new MutationObserver(() => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => { queued = false; scan(); });
  });
  function start() {
    observer.observe(document.body, { childList: true, subtree: true });
    scan();
  }
  window.BluvvyBundleOptions = { readSets, convert };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
